import React from 'react';
import FormsRow from '../../../components/molecules/Form/components/FormsRow/FormsRow';
import FormInput from '../../../components/molecules/Form/components/FormInput/FormInput';
import FormCheckbox from '../../../components/molecules/Form/components/FormCheckbox/FormCheckbox';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import { FormUtils, useFormContext } from '../../../components/molecules/Form';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { InteractionStatus } from '../../../types/InteractionStatus';
import { ImageCropperModifier } from '../../../components/molecules/ImageCropper/ImageCropper.modifier';
import {
  ImagePreview,
  ImageUpload,
  ImageUploadDescription,
  ImageUploadHeadline,
  ImageUploadParams,
  PreviewSuspense,
  PreviewWithCropper,
  useSingleImageUpload,
} from '../../../_lib/components/ImageUpload';
import { AppLibraryImage } from '../../../types/api/ImageType';
import { isValid } from '../../../_lib/FileUpload/utils/validateImage';
import { FormSubmitButton } from '../../../components/molecules/Form/components/FormSubmitButton/FormSubmitButton';
import { RequiredHelpPhrase } from '../../../_lib/Form2';
import { CollapsibleModifier } from '../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay.modifier';

const formId = 'add-link-form';

export function AppLibraryAddOrUpdateFormTemplate({ initialImage }: { initialImage?: AppLibraryImage }) {
  const { t } = useSonTranslation('AppLibrary');
  const statusMessage = {
    [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('addOrUpdateForm.status.success')),
    [InteractionStatus.SENDING]: FormUtils.createStatusMessage(t('addOrUpdateForm.status.sending')),
    [InteractionStatus.FAIL]: FormUtils.createStatusMessage(t('addOrUpdateForm.status.fail')),
  };

  const { inputs, onChange, getStatus, submitActionEnabled } = useFormContext();
  const { title, url, optional } = inputs as any;

  const { selectedPreview, inputProps, labelProps, constraints, suspendPreviewProps, imageUploadParamsProps } =
    useSingleImageUpload(formId);

  return (
    <>
      <ImageUploadHeadline />
      <ImageUploadDescription constraints={constraints} />
      <ImageUpload inputProps={inputProps} labelProps={labelProps}>
        <PreviewSuspense {...suspendPreviewProps}>
          {isValid(selectedPreview)
            ? (
                <PreviewWithCropper
                  selectedPreview={selectedPreview}
                  imageCropperModifier={ImageCropperModifier.FIXED_SQUARE_60_60}
                />
              )
            : (
                <ImagePreview image={initialImage} />
              )}
        </PreviewSuspense>
        <ImageUploadParams {...imageUploadParamsProps} hasInitialImage={!!initialImage} />
      </ImageUpload>
      <RequiredHelpPhrase />
      <FormsRow>
        <FormInput
          id={`${formId}-${title.name}`}
          type={title.type}
          name={title.name}
          min={title.constraints?.min}
          max={title.constraints?.max}
          required={title.required}
          value={title.value}
          labelTitle={t('addOrUpdateForm.input.title')}
          maxLength={title.constraints?.maxLength}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.name, event.target.value)}
        />
      </FormsRow>
      <FormsRow>
        <FormInput
          id={`${formId}-${url.name}`}
          type={url.type}
          name={url.name}
          required={url.required}
          value={url.value}
          pattern={url.constraints.pattern}
          maxLength={url.constraints?.maxLength}
          labelTitle={t('addOrUpdateForm.input.url')}
          labelHelpText={t('addOrUpdateForm.input.urlHelpText')}
          labelHelpTextModifier={CollapsibleModifier.FORM_HELP_RIGHT_CENTER}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.name, event.target.value)}
        />
      </FormsRow>
      {optional && (
        <FormsRow>
          <FormCheckbox
            layout="switch"
            id={`${formId}-${optional.name}`}
            name={optional.name}
            required={optional.required}
            labelTitle={t('addOrUpdateForm.optionalLink')}
            checked={optional.checked}
            value={optional.value}
            onChange={optional.onChange}
          />
        </FormsRow>
      )}
      <FormSubmitButton buttonLabel={t('addOrUpdateForm.submit')} enabled={submitActionEnabled()} />
      <LiveRegion htmlTag="div" invisible={false} ariaAtomic>
        {getStatus(statusMessage)}
      </LiveRegion>
    </>
  );
}
